import { Logger, LogLevel } from '@/utils'
import {
  OpenExcelCommand,
  OpenExcelParameter,
  ScreenshotParameter,
  ScreenshotCommand,
  CommandType,
  ReceiptPrintParameter,
  ImeOffCommand,
  OpenHandyTerminalCommand,
  SendIcTagReadingCommand,
  SendIcTagWritingCommand,
  SendAFIWritingCommand,
  ReWriteCardReadCommand,
  ReWriteCardWriteCommand,
  ReWriteCardEjectCommand,
  ReWriteCardEraseCommand,
  ReWriteCardParameter,
  ReWriteCardParameterFromJSON,
  SendReWriteCardWriteCommandData,
  SendReWriteCardWriteCommandToJSON,
  IcTagReadingParameter,
  IcTagReadingParameterFromJSON,
  IcTagWritingParameter,
  SendIcTagWritingCommandToJSON,
  SendIcTagWritingCommandData,
  IcTagWritingParameterFromJSON,
  AFIWritingParameter,
  AFIWritingParameterFromJSON,
  SendAFIWritingCommandToJSON,
  SendAFIWritingCommandData,
  SendAutoLibCommandToJSON,
  SendAutoLibCommandData,
  SendAutoLibCommand,
  SendIcTagReadingCommandToJSON,
  ReceiptPrintParameterToJSON,
  ReWriteCardReadCancelCommand,
  ConnectParameter,
  ConnectParameterFromJSON,
  defaultConnectParameter,
  ImeOnCommand,
  SendImeOnCommandToJSON,
  ImeOnCommandData,
  FelicaParameter,
  FelicaReadCommand,
  FelicaParameterFromJSON,
  FelicaReadCancelCommand
} from '@/types'
import ReceiptHistoryService from '@/services/ReceiptHistoryService'
import { EventBus } from '@/plugins/event_bus'
import { RECEIVED_CONNECT_PARAMETER } from '@/config/event'

export const WebSocketState = {
  Connecting: 0,
  Open: 1,
  Closing: 2,
  Closed: 3,
  ReceivedConnectParameter: 4 // 公式のreadyStateには存在しない値。接続レスポンスを受け取ってから処理をしたいので…
} as const
export type WebSocketState = typeof WebSocketState[keyof typeof WebSocketState]

interface connectCallback {
  (state: WebSocketState): void
}

export default class WebSocketService {
  logger: Logger

  ws: WebSocket | null

  connectParameter: ConnectParameter = defaultConnectParameter()

  constructor() {
    this.logger = new Logger(LogLevel.DEBUG)
    this.ws = null
  }

  public connect(callback?: connectCallback) {
    this.logger.info('[WebSocketService] connect() start')

    const support: any =
      'MozWebSocket' in window
        ? 'MozWebSocket'
        : 'WebSocket' in window
        ? 'WebSocket'
        : null

    if (support == null) {
      this.logger.error('[WebSocketService] websocket not supported')
      return
    }

    if (this.ws != null) {
      if (this.isOpen()) {
        this.logger.info('[WebSocketService] websocket already opened')
        if (callback) {
          callback(WebSocketState.Open)
        }
        return
      } else if (this.isConnecting()) {
        this.logger.warn('[WebSocketService] websocket yet connecting')
        if (callback) {
          callback(WebSocketState.Connecting)
        }
        return
      }

      this.ws.close()
    }

    const url: string = process.env.WS_URL || 'ws://localhost:81/'
    this.logger.info('[WebSocketService] url:' + url)
    try {
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.logger.info('[WebSocketService] onopen()')
        console.log('ws', this.ws)
        if (callback) {
          callback(WebSocketState.Open)
        }
      }

      this.ws.onclose = (event: unknown) => {
        this.logger.info('[WebSocketService] onclose()', event)
        if (callback) {
          callback(WebSocketState.Closed)
        }
      }

      this.ws.onerror = (event: unknown) => {
        this.logger.info('[WebSocketService] onerror()', event)
        if (callback) {
          callback(WebSocketState.Closed)
        }
      }

      this.ws.onmessage = (event: any) => {
        const jsonData = ConnectParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.Connect) {
          this.logger.info('[WebSocketService] from message', event.data)

          this.connectParameter = jsonData

          // HACK イベントバス以外の代案があれば変更する事
          EventBus.emit(RECEIVED_CONNECT_PARAMETER)
        }
      }
    } catch (ex) {
      console.log('[WebSocketService] WebSocketが接続されていません。')
    }
  }

  public disconnect(): void {
    if (this.ws) {
      this.ws.close()
    }
    this.logger.info('close')
  }

  // ある時間までに接続できなかった場合、強制切断をできるように設定
  public disconnectTimeout(ms: number): void {
    setTimeout(() => {
      if (!this.isOpen()) {
        this.disconnect()
      }
    }, ms)
  }

  public isOpen(): boolean {
    return this.ws?.readyState === WebSocketState.Open
  }

  public isConnecting(): boolean {
    return this.ws?.readyState === WebSocketState.Connecting
  }

  public isClosed(): boolean {
    return (
      this.ws?.readyState === undefined ||
      this.ws?.readyState === WebSocketState.Closed
    )
  }

  public get isFetchConnectParameter(): boolean {
    return this.connectParameter.command !== ''
  }

  public get reWriteCardEnabled(): boolean {
    return this.connectParameter.data.rewritecardEnabled
  }

  public get authKey(): string {
    return this.connectParameter.data.authKey
  }

  public sendScreenshotMessage(
    screenshotParameter?: ScreenshotParameter
  ): void {
    this.logger.debug('sendScreenshotMessage()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendScreenshotMessage(screenshotParameter)
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    if (screenshotParameter === undefined) {
      screenshotParameter = {
        x: 0,
        y: 0,
        width: window.innerWidth,
        height: window.innerHeight
      }
    }

    const sendCommand: ScreenshotCommand = {
      command: CommandType.ScreenShot,
      x: screenshotParameter.x,
      y: screenshotParameter.y,
      width: screenshotParameter.width,
      height: screenshotParameter.height
    }

    const sendCommandString = JSON.stringify(sendCommand)

    setTimeout(() => {
      if (this.ws) {
        this.ws.send(sendCommandString)
        this.logger.info('send message:', sendCommandString)
      }
    }, 500)
  }

  public sendDialogScreenshotMessage(element: HTMLElement | Element): void {
    const rect = element.getBoundingClientRect()
    const leftBottomY = window.innerHeight - Math.round(rect.bottom)
    const adjustValue = 4
    const screenshotParameter: ScreenshotParameter = {
      x: Math.round(rect.left) + adjustValue,
      y: leftBottomY,
      width: Math.round(rect.width) - adjustValue,
      height: Math.round(rect.height) - adjustValue
    }

    this.sendScreenshotMessage(screenshotParameter)
  }

  public sendOpenExcelMessage(openExcelParameter: OpenExcelParameter): void {
    this.logger.debug('sendOpenExcelMessage()', openExcelParameter)

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendOpenExcelMessage(openExcelParameter)
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    const sendCommand: OpenExcelCommand = {
      command: CommandType.OpenExcel,
      formatType: openExcelParameter.formatType,
      csvUrl: openExcelParameter.csvUrl,
      templateUrl: openExcelParameter.templateUrl
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)
    }
  }

  public sendReceiptPrintMessage(
    receiptPrintParameter: ReceiptPrintParameter
  ): void {
    this.logger.debug(
      'sendReceiptPrintMessage()',
      ReceiptPrintParameterToJSON(receiptPrintParameter)
    )

    if (receiptPrintParameter.name !== undefined) {
      new ReceiptHistoryService().save(
        receiptPrintParameter.command,
        receiptPrintParameter.name,
        receiptPrintParameter.data,
        receiptPrintParameter.subject ?? '',
        receiptPrintParameter.maxPrintCount ?? undefined,
        receiptPrintParameter.customerCode ?? undefined
      )
    }

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendReceiptPrintMessage(receiptPrintParameter)
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    const sendCommandString = JSON.stringify(
      ReceiptPrintParameterToJSON(receiptPrintParameter)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)
    }
  }

  public sendImeOnMessage(data: ImeOnCommandData): void {
    this.logger.debug('sendImeOnMessage()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendImeOnMessage(data)
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    const sendCommand: ImeOnCommand = {
      command: CommandType.ImeOn,
      data
    }

    const sendCommandString = JSON.stringify(
      SendImeOnCommandToJSON(sendCommand)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)
    }
  }

  public sendImeOffMessage(): void {
    this.logger.debug('sendImeOffMessage()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendImeOffMessage()
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    const sendCommand: ImeOffCommand = {
      command: CommandType.ImeOff
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)
    }
  }

  public sendOpenHandyTerminal(): void {
    this.logger.debug('sendOpenHandyTerminal()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendOpenHandyTerminal()
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    const sendCommand: OpenHandyTerminalCommand = {
      command: CommandType.OpenHandyTerminal
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)
    }
  }

  public sendICTagReading(
    agencyCode: string,
    callback: (isConnect: boolean, parameter?: IcTagReadingParameter) => void
  ): void {
    this.logger.debug('sendICTagReading()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendICTagReading(agencyCode, callback)
        } else {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        }
      })
      return
    }

    const sendCommand: SendIcTagReadingCommand = {
      command: CommandType.ICTagReading,
      agencyCode
    }

    const sendCommandString = JSON.stringify(
      SendIcTagReadingCommandToJSON(sendCommand)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)
        const jsonData = IcTagReadingParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command !== CommandType.ICTagReading) {
          console.warn('sendICTagReading() コマンドタイプ不一致', jsonData)
          return
        }

        callback(true, jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendICTagWriting(
    data: SendIcTagWritingCommandData,
    callback: (isConnect: boolean, parameter?: IcTagWritingParameter) => void
  ): void {
    this.logger.debug('sendICTagWriting()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendICTagWriting(data, callback)
        } else {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        }
      })
      return
    }

    const sendCommand: SendIcTagWritingCommand = {
      command: CommandType.ICTagWriting,
      name: 'ICタグ書込',
      data: {
        industryCode: data.industryCode,
        branchType: data.branchType,
        agencyCode: data.agencyCode,
        noFlag: data.noFlag,
        no: data.no
      }
    }

    const sendCommandString = JSON.stringify(
      SendIcTagWritingCommandToJSON(sendCommand)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)
        const jsonData = IcTagWritingParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command !== CommandType.ICTagWriting) {
          console.warn('sendICTagWriting() コマンドタイプ不一致', jsonData)
          return
        }

        callback(true, jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendAFIWriting(
    data: SendAFIWritingCommandData,
    callback: (isConnect: boolean, parameter?: AFIWritingParameter) => void
  ): void {
    this.logger.debug('sendAFIWriting()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendAFIWriting(data, callback)
        } else {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        }
      })
      return
    }

    const sendCommand: SendAFIWritingCommand = {
      command: CommandType.AFIWriting,
      name: 'ICタグAFI書込',
      data
    }

    const sendCommandString = JSON.stringify(
      SendAFIWritingCommandToJSON(sendCommand)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)
        const jsonData = AFIWritingParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command !== CommandType.AFIWriting) {
          console.warn('sendAFIWriting() コマンドタイプ不一致', jsonData)
          return
        }

        callback(true, jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendAutoLib(data: SendAutoLibCommandData): void {
    this.logger.debug('sendAutoLib()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state === WebSocketState.Open) {
          this.sendAutoLib(data)
        } else {
          this.logger.warn('websocketが接続されていません。')
        }
      })
      return
    }

    const sendCommand: SendAutoLibCommand = {
      command: CommandType.AutoLib,
      name: '自動書庫連携',
      data
    }

    const sendCommandString = JSON.stringify(
      SendAutoLibCommandToJSON(sendCommand)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)
    }
  }

  public sendReWriteCardRead(
    callback: (isConnect: boolean, parameter?: ReWriteCardParameter) => void,
    completeCallback: (
      isConnect: boolean,
      parameter?: ReWriteCardParameter
    ) => void
  ): void {
    this.logger.debug('sendReWriteCardRead()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else if (!this.reWriteCardEnabled) {
          this.logger.warn('端末設定のリライトカード機能が無効になっています。')
          callback(false)
        } else {
          this.sendReWriteCardRead(callback, completeCallback)
        }
      })
      return
    }

    const sendCommand: ReWriteCardReadCommand = {
      command: CommandType.ReWriteCardRead,
      name: 'リライトカード読込'
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = ReWriteCardParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.ReWriteCardRead) {
          callback(true, jsonData)
          return
        }

        if (jsonData.command === CommandType.ReWriteCardReadComplete) {
          completeCallback(true, jsonData)
          return
        }

        console.warn('sendReWriteCardRead() コマンドタイプ不一致', jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendReWriteCardReadCancel(
    callback: (isConnect: boolean, parameter?: ReWriteCardParameter) => void
  ): void {
    this.logger.debug('sendReWriteCardReadCancel()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else if (!this.reWriteCardEnabled) {
          this.logger.warn('端末設定のリライトカード機能が無効になっています。')
          callback(false)
        } else {
          this.sendReWriteCardReadCancel(callback)
        }
      })
      return
    }

    const sendCommand: ReWriteCardReadCancelCommand = {
      command: CommandType.ReWriteCardReadCancel,
      name: 'リライトカード読込キャンセル'
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = ReWriteCardParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.ReWriteCardReadCancel) {
          callback(true, jsonData)
          return
        }

        console.warn(
          'sendReWriteCardReadCancel() コマンドタイプ不一致',
          jsonData
        )
      }
    } else {
      callback(false)
    }
  }

  public sendReWriteCardWrite(
    data: SendReWriteCardWriteCommandData,
    callback: (isConnect: boolean, parameter?: ReWriteCardParameter) => void,
    completeCallback: (
      isConnect: boolean,
      parameter?: ReWriteCardParameter
    ) => void
  ): void {
    this.logger.debug('sendReWriteCardWrite()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else if (!this.reWriteCardEnabled) {
          this.logger.warn('端末設定のリライトカード機能が無効になっています。')
          callback(false)
        } else {
          this.sendReWriteCardWrite(data, callback, completeCallback)
        }
      })
      return
    }

    const sendCommand: ReWriteCardWriteCommand = {
      command: CommandType.ReWriteCardWrite,
      name: 'リライトカード書込',
      data
    }

    const sendCommandString = JSON.stringify(
      SendReWriteCardWriteCommandToJSON(sendCommand)
    )

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = ReWriteCardParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.ReWriteCardWrite) {
          callback(true, jsonData)
          return
        }

        if (jsonData.command === CommandType.ReWriteCardWriteComplete) {
          completeCallback(true, jsonData)
          return
        }

        console.warn('sendReWriteCardWrite() コマンドタイプ不一致', jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendReWriteCardEject(
    callback: (isConnect: boolean, parameter?: ReWriteCardParameter) => void
  ): void {
    this.logger.debug('sendReWriteCardEject()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else if (!this.reWriteCardEnabled) {
          this.logger.warn('端末設定のリライトカード機能が無効になっています。')
          callback(false)
        } else {
          this.sendReWriteCardEject(callback)
        }
      })
      return
    }

    const sendCommand: ReWriteCardEjectCommand = {
      command: CommandType.ReWriteCardEject,
      name: 'リライトカード排出'
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = ReWriteCardParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.ReWriteCardEject) {
          callback(true, jsonData)
          return
        }

        console.warn('sendReWriteCardEject() コマンドタイプ不一致', jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendReWriteCardErase(
    callback: (isConnect: boolean, parameter?: ReWriteCardParameter) => void
  ): void {
    this.logger.debug('sendReWriteCardErase()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else if (!this.reWriteCardEnabled) {
          this.logger.warn('端末設定のリライトカード機能が無効になっています。')
          callback(false)
        } else {
          this.sendReWriteCardErase(callback)
        }
      })
      return
    }

    const sendCommand: ReWriteCardEraseCommand = {
      command: CommandType.ReWriteCardErase,
      name: 'リライトカード消去'
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = ReWriteCardParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.ReWriteCardErase) {
          callback(true, jsonData)
          return
        }

        console.warn('sendReWriteCardErase() コマンドタイプ不一致', jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendFelicaRead(
    callback: (isConnect: boolean, parameter?: FelicaParameter) => void,
    completeCallback: (isConnect: boolean, parameter?: FelicaParameter) => void
  ): void {
    this.logger.debug('sendFelicaRead()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else {
          this.sendFelicaRead(callback, completeCallback)
        }
      })
      return
    }

    const sendCommand: FelicaReadCommand = {
      command: CommandType.FelicaRead,
      name: 'Felica読込'
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = FelicaParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.FelicaRead) {
          callback(true, jsonData)
          return
        }

        if (jsonData.command === CommandType.FelicaReadComplete) {
          completeCallback(true, jsonData)
          return
        }

        console.warn('sendFelicaRead() コマンドタイプ不一致', jsonData)
      }
    } else {
      callback(false)
    }
  }

  public sendFelicaReadCancel(
    callback: (isConnect: boolean, parameter?: FelicaParameter) => void
  ): void {
    this.logger.debug('sendFelicaReadCancel()')

    if (this.ws === null || !this.isOpen()) {
      this.connect(state => {
        if (state !== WebSocketState.Open) {
          this.logger.warn('websocketが接続されていません。')
          callback(false)
        } else {
          this.sendFelicaReadCancel(callback)
        }
      })
      return
    }

    const sendCommand: FelicaReadCancelCommand = {
      command: CommandType.FelicaReadCancel,
      name: 'Felica読込キャンセル'
    }

    const sendCommandString = JSON.stringify(sendCommand)

    if (this.ws) {
      this.ws.send(sendCommandString)
      this.logger.info('send message:', sendCommandString)

      this.ws.onmessage = (event: any) => {
        this.logger.info('from message', event.data)

        if (JSON.parse(event.data).command === CommandType.Connect) {
          this.connectParameter = ConnectParameterFromJSON(
            JSON.parse(event.data)
          )
          return
        }

        const jsonData = FelicaParameterFromJSON(JSON.parse(event.data))

        if (jsonData.command === CommandType.FelicaReadCancel) {
          callback(true, jsonData)
          return
        }

        console.warn('sendFelicaReadCancel() コマンドタイプ不一致', jsonData)
      }
    } else {
      callback(false)
    }
  }
}
